import { useCallback, useEffect, useState } from 'react';
import Spinner from '../Spinner/Spinner';
import './StudentComparison.scss';
import {
  IGender,
  IJurisdiction,
  IRace,
  IComparisonDropdownResponse,
  ComparisonReportProps,
  fetchComparisonDropdowns,
  fetchComparisonReport,
  IComparisonReportData,
  COLUMN_MAPPING,
  DESCRIPTION_COLUMN,
  IComparisonReportResponse,
  downloadComparisonReport,
} from './models';
import { useSelector } from 'react-redux';
import { selectControls } from '../../redux/controls';

const staticMenuStyleJur = {
  width: '296px',
};

const staticMenuStyleGen = {
  width: '190px',
};


export const StudentComparison = ({
  admin,
  setReportOnScreen,
  printSignal,
  exportSignal,
}: ComparisonReportProps): JSX.Element => {
  const controls = useSelector(selectControls);

  const [abortController, setAbortController] = useState<AbortController>();
  const [isLoading, setIsLoading] = useState<boolean>(false); // for dropdowns load
  const [isReportLoading, setIsReportLoading] = useState<boolean>(false); // for report load
  const [isPrinting, setIsPrinting] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);

  const [dropdownResponse, setDropdownResponse] =
    useState<IComparisonDropdownResponse | null>(null);
  const [jurisdictionList, setJurisdictionList] = useState<
    IJurisdiction[] | null
  >(null);
  const [genderList, setGenderList] = useState<IGender[] | null>(null);
  const [raceList, setRaceList] = useState<IRace[] | null>(null);
  const [selectedJurisdiction, setSelectedJurisdiction] =
    useState<IJurisdiction | null>(null);
  const [selectedGender, setSelectedGender] = useState<IGender | null>(null);
  const [selectedRace, setSelectedRace] = useState<IRace | null>(null);
  const [compReport, setCompReport] = useState<IComparisonReportData[] | null>(null);
  const [noCompReport, setNoCompReport] = useState<boolean>(false);

  const onClickReportJurisdiction = useCallback((jur: IJurisdiction) => {
    if (jur && typeof jur !== null) {
      setSelectedJurisdiction(jur);
    }
  }, []);

  const onClickReportGender = useCallback((gen: IGender) => {
    if (gen && typeof gen !== null) {
      setSelectedGender(gen);
    }
  }, []);

  const onClickReportRace = useCallback((rac: IRace) => {
    if (rac && typeof rac !== null) {
      setSelectedRace(rac);
    }
  }, []);

  useEffect(() => {
    if (printSignal && !isPrinting) {
      setIsPrinting(true);
      const printableDiv = document.getElementById('section-to-print');
      if (printableDiv) {
        window.print();
      }
      setIsPrinting(false);
    }
  }, [isPrinting, printSignal]);

  useEffect(() => {
    if (exportSignal && !isExporting && compReport !== null) {
      setIsExporting(true);
      downloadComparisonReport(compReport, exportSignal).then(() => {
        setIsExporting(false);
      }).catch((error) => {
        console.error('Failed to download Comparison report:', error);
        setIsExporting(false);
      });
    }
  }, [exportSignal, isExporting, compReport]);

  useEffect(() => {
    return () => {
      abortController?.abort();
    };
  }, [abortController]);

  useEffect(() => {
    if (dropdownResponse !== null) return;
    const ddAbortController = new AbortController();
    setAbortController(ddAbortController);
    setIsLoading(true);
    fetchComparisonDropdowns(admin, ddAbortController).then(
      (response) => {
        if (response.__typename === 'ErrorResponse') {
          // handle error
          setDropdownResponse(null);
        } else if (response.__typename === 'StudentDropdownResponse') {
          setDropdownResponse(response as IComparisonDropdownResponse);
        }
        setIsLoading(false);
      }
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  useEffect(() => {
    if (dropdownResponse) {
      const jurList = Object.keys(dropdownResponse.jurisdictionList)
        .map((key) => {
          return {
            code: dropdownResponse.jurisdictionList[key].code,
            value: dropdownResponse.jurisdictionList[key],
          };
        })
        .sort((a, b) => a.value.sortOrder - b.value.sortOrder);
      setJurisdictionList(jurList);
      setSelectedJurisdiction(jurList[0]);

      const genList = Object.keys(dropdownResponse.genderList)
        .map((key) => {
          return {
            code: parseInt(key),
            value: dropdownResponse.genderList[key],
          };
        })
        .sort((a, b) => a.code - b.code);
      setGenderList(genList);
      setSelectedGender(genList[0]);

      const raceList = Object.keys(dropdownResponse.raceList)
        .map((key) => {
          return {
            code: parseInt(key),
            value: dropdownResponse.raceList[key],
          };
        })
        .sort((a, b) => a.code - b.code);
      setRaceList(raceList);
      setSelectedRace(raceList[0]);
    }
  }, [dropdownResponse]);

  const getReport = useCallback(() => {
    if (selectedJurisdiction && selectedGender && selectedRace) {
      const abortController = new AbortController();
      setAbortController(abortController);
      setIsReportLoading(true);
      setIsLoading(true);
      setNoCompReport(false);
      setReportOnScreen(false);
      fetchComparisonReport(admin, selectedJurisdiction, selectedGender, selectedRace, abortController).then(
        (response) => {
          if (response.__typename === 'ErrorResponse') {
            // handle error
            setNoCompReport(true);
          } else if (response.__typename === 'ComparisonReportResponse') {
            // handle response
            const compReport = (response as IComparisonReportResponse).comparisonReport;

            if (compReport?.reportDataExists) {
              setNoCompReport(false);
              setCompReport(compReport.reportData);
              setReportOnScreen(!!response);
            } else {
              setNoCompReport(true);
              setReportOnScreen(!!response);
            }
          }
        }
      ).finally(() => {
        setIsReportLoading(false);
        setIsLoading(false);
      });
    }
  }, [selectedJurisdiction, selectedGender, selectedRace, admin, setReportOnScreen]);

  return (
    <>
      {isReportLoading &&
        <div className="sc-spinner-container d-flex justify-content-center align-items-center">
          <Spinner size={120} containerClassName="sc-spinner-container" />
        </div>
      }
      <section className="sc-container" id="comparison-section">
        <div className="sc-spinner-container d-flex justify-content-center align-items-center">
          {isLoading && !isReportLoading && (
            <Spinner size={120} containerClassName="sc-spinner-container" />
          )}
        </div>
        <div className="container sc-dropdowns-container">
          <div className="row justify-content-end">
            <div className="dropdown sc-dropdown col-3">
              <span className="sc-dd-title">
                {controls?.Rpt_Comparison_Jurisdiction?.controlText}
              </span>
              <button
                className="dropdown-toggle nqt-dropdown-btn btn "
                type="button"
                id="dropdownJurisdiction"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <span className="dropdown-interior">
                  {selectedJurisdiction
                    ? selectedJurisdiction?.value?.label
                    : controls?.Rpt_Comparison_Jurisdiction?.controlText}
                </span>
                <span className="dropdown-arrow"></span>
              </button>
              <ul
                className="dropdown-menu"
                style={staticMenuStyleJur}
                aria-labelledby="dropdownReports"
              >
                {jurisdictionList &&
                  jurisdictionList.length > 0 &&
                  jurisdictionList.map((jurObj, ind) => (
                    <li key={`jr-rpt-${ind}`}>
                      <a
                        className={
                          'dropdown-item' +
                          (jurObj.code === selectedJurisdiction?.code
                            ? ' selected'
                            : '')
                        }
                        onClick={(e) => {
                          e.preventDefault();
                          onClickReportJurisdiction(jurObj);
                        }}
                        href="#"
                      >
                        {jurObj.value.label}
                      </a>
                    </li>
                  ))}
              </ul>
            </div>

            <div className="dropdown sc-dropdown col-2">
              <span className="sc-dd-title">
                {controls?.Rpt_Comparison_Gender?.controlText}
              </span>
              <button
                className="dropdown-toggle nqt-dropdown-btn btn "
                type="button"
                id="dropdownGender"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <span className="dropdown-interior">
                  {selectedGender
                    ? selectedGender?.value
                    : controls?.Rpt_Comparison_Gender?.controlText}
                </span>
                <span className="dropdown-arrow"></span>
              </button>
              <ul
                className="dropdown-menu"
                style={staticMenuStyleGen}
                aria-labelledby="dropdownReports"
              >
                {genderList &&
                  genderList.length > 0 &&
                  genderList.map((genObj, ind) => (
                    <li key={`gn-rpt-${ind}`}>
                      <a
                        className={
                          'dropdown-item' +
                          (genObj.code === selectedGender?.code
                            ? ' selected'
                            : '')
                        }
                        onClick={(e) => {
                          e.preventDefault();
                          onClickReportGender(genObj);
                        }}
                        href="#"
                      >
                        {genObj.value}
                      </a>
                    </li>
                  ))}
              </ul>
            </div>

            <div className="dropdown sc-dropdown col-3">
              <span className="sc-dd-title">
                {controls?.Rpt_Comparison_Race?.controlText}
              </span>
              <button
                className="dropdown-toggle nqt-dropdown-btn btn "
                type="button"
                id="dropdownRace"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <span className="dropdown-interior">
                  {selectedRace
                    ? selectedRace?.value
                    : controls?.Rpt_Comparison_Race?.controlText}
                </span>
                <span className="dropdown-arrow"></span>
              </button>
              <ul
                className="dropdown-menu"
                style={staticMenuStyleJur}
                aria-labelledby="dropdownReports"
              >
                {raceList &&
                  raceList.length > 0 &&
                  raceList.map((racObj, ind) => (
                    <li key={`rc-rpt-${ind}`}>
                      <a
                        className={
                          'dropdown-item' +
                          (racObj.code === selectedRace?.code
                            ? ' selected'
                            : '')
                        }
                        onClick={(e) => {
                          e.preventDefault();
                          onClickReportRace(racObj);
                        }}
                        href="#"
                      >
                        {racObj.value}
                      </a>
                    </li>
                  ))}
              </ul>
            </div>
          </div>
          <div className="row mt-3 me-1">
            <div className="col d-flex justify-content-end">
              <button
                className="nqt-btn sid-create-btn nqt-sc--report-button"
                type='submit'
                onClick={() => {
                  getReport();
                }}
              >
                {controls?.Rpt_CreateReport?.controlText}
              </button>
            </div>
          </div>
        </div>
      </section>
      { noCompReport && !isReportLoading && (
        <div className="sid-error-message">
          <p className="error-message">
            {controls?.Rpt_ReportNoData?.controlText}
          </p>
        </div>
      )}
      <section className="sc-container section-to-print" id="section-to-print">
        { !noCompReport && compReport !== null && !isReportLoading && compReport.map( (rpt, idx) => {
          return <div key={`report-${idx}`}>
            {
              rpt.headerInfo && Object.keys(rpt.headerInfo).length > 0 && (
                Object.keys(rpt.headerInfo).map((key, index) => (
                  <div key={`header-info-${index}`} className={`sid-header-info ${index === 0 && 'sc-header-report-name'}`}>
                    <span className="sid-header-label">{key}</span>
                    <span className="sid-header-value">
                      {rpt.headerInfo[key as keyof typeof rpt.headerInfo]}
                    </span>
                  </div>
                ))
              )}
            {rpt.rows.length > 0 && (
              <div className="sid-table-container">
                <table className="sid-table">
                  <thead>
                    <tr className="nqt-table-header-row">
                      {rpt.rows
                        .filter((row) => row.rowType === 0)
                        .map((row, index) => {
                          return row.rowCells.map((cell, ind) => (
                            <td
                              className={`nqt-table-header-cell  ${
                                COLUMN_MAPPING[ind] !== DESCRIPTION_COLUMN &&
                              'text-center align-middle'
                              }`}
                              key={`table-header-${index}-${ind}`}
                              colSpan={cell.colSpan}
                              rowSpan={cell.rowSpan}
                            >
                              {cell.stringValue}
                            </td>
                          ));
                        })}
                    </tr>
                  </thead>
                  <tbody>
                    {rpt.rows
                      .filter((row) => row.rowType === 1)
                      .map((row, index) => {
                        return (
                          <tr
                            className="nqt-table-body-row"
                            key={`table-body-row-${index}`}
                          >
                            {row.rowCells.map((cell, ind) => (
                              <td
                                className={`nqt-table-body-cell ${
                                  COLUMN_MAPPING[ind] !== DESCRIPTION_COLUMN &&
                                'text-center align-middle'
                                }`}
                                key={`table-body-row-cell-${index}-${ind}`}
                                colSpan={cell.colSpan}
                                rowSpan={cell.rowSpan}
                              >
                                {cell.stringValue}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                  </tbody>
                  <tfoot>
                    {rpt.rows
                      .filter((row) => row.rowType === 3)
                      .map((row, index) => {
                        return (
                          <tr
                            className="nqt-table-footer-row"
                            key={`table-footer-row-${index}`}
                          >
                            {row.rowCells.map((cell, ind) => (
                              <td
                                className={`nqt-table-footer-cell nqt-table-body-cell ${
                                  COLUMN_MAPPING[ind] !== DESCRIPTION_COLUMN &&
                                'text-center align-middle'
                                }`}
                                key={`table-footer-row-cell-${index}-${ind}`}
                                colSpan={cell.colSpan}
                                rowSpan={cell.rowSpan}
                              >
                                {cell.stringValue}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                  </tfoot>
                </table>
              </div>
            )}
            <div className="si-notes-container">
              {rpt.notes.map((note, index) => (
                <p key={`note-${index}`} className="sid-notes-text">
                  {note}
                </p>
              ))}
            </div>
            <div className="si-notes-container">
              {rpt.footnotes.map((note, index) => (
                <p key={`note-${index}`} className="sid-notes-text">
                  {note.footnoteText}
                </p>
              ))}
            </div>
          </div>;
        })}
      </section>
    </>
  );
};

export default StudentComparison;
